import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import MainContent from '../components/MainContent';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import {
  CARD,
  SMS_BASE,
  SmsUnavailable,
  StatusChip,
  UNKNOWN_EXPLANATION,
  describeAudience,
  formatDateTime,
  isActive,
} from '../components/sms/smsShared';

const POLL_MS = 5000;
const PAGE_SIZE = 50;

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'sent', label: 'Sent' },
  { key: 'failed', label: 'Failed' },
  { key: 'unknown', label: 'Unknown' },
];

const SEGMENTS = [
  { key: 'sent', label: 'Sent', bar: 'bg-green-600', text: 'text-green-700' },
  { key: 'failed', label: 'Failed', bar: 'bg-red-500', text: 'text-red-700' },
  {
    key: 'unknown',
    label: 'Unknown',
    bar: 'bg-amber-400',
    text: 'text-amber-700',
  },
  {
    key: 'remaining',
    label: 'Waiting',
    bar: 'bg-gray-200',
    text: 'text-gray-500',
  },
];

function Progress({ campaign }) {
  const counts = campaign.counts ?? {};
  const values = {
    sent: counts.sent ?? 0,
    failed: counts.failed ?? 0,
    unknown: counts.unknown ?? 0,
    remaining: (counts.pending ?? 0) + (counts.sending ?? 0),
  };
  const total = campaign.recipient_count || 1;
  const done = values.sent + values.failed + values.unknown;
  const percent = Math.min(Math.round((done / total) * 100), 100);

  return (
    <section className={`${CARD} p-5 space-y-4`}>
      <div className="flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-gray-900">Progress</h2>
        <span className="text-sm tabular-nums text-gray-700">
          {percent}% processed
        </span>
      </div>
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {SEGMENTS.map((segment) =>
          values[segment.key] > 0 ? (
            <div
              key={segment.key}
              className={`${segment.bar} h-full transition-all`}
              style={{ width: `${(values[segment.key] / total) * 100}%` }}
            />
          ) : null
        )}
      </div>
      <dl className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div>
          <dt className="text-xs text-gray-500">Recipients</dt>
          <dd className="text-xl font-semibold tabular-nums text-gray-900">
            {(campaign.recipient_count ?? 0).toLocaleString()}
          </dd>
        </div>
        {SEGMENTS.map((segment) => (
          <div key={segment.key}>
            <dt className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className={`h-2 w-2 rounded-full ${segment.bar}`} />
              {segment.label}
            </dt>
            <dd
              className={`text-xl font-semibold tabular-nums ${segment.text}`}
            >
              {values[segment.key].toLocaleString()}
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-gray-500">
        Sent means Advanta accepted the message; it isn&apos;t a delivery
        receipt. {UNKNOWN_EXPLANATION}
      </p>
    </section>
  );
}

function MessagesTable({ campaignId, active }) {
  const [filter, setFilter] = useState('');
  const [skip, setSkip] = useState(0);

  const { data, isLoading, error } = useQuery(
    ['sms-campaign-messages', campaignId, filter, skip],
    async () =>
      (
        await axiosInstancev2.get(`${SMS_BASE}/${campaignId}/messages`, {
          params: {
            limit: PAGE_SIZE,
            skip,
            ...(filter ? { status: filter } : {}),
          },
        })
      ).data,
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
      refetchInterval: active ? POLL_MS : false,
    }
  );
  const messages = data?.messages ?? [];
  const total = data?.total ?? 0;

  return (
    <section className={`${CARD} overflow-hidden`}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-5 pb-3">
        <h2 className="text-base font-semibold text-gray-900">Messages</h2>
        <div
          className="flex gap-1 bg-gray-50 border border-gray-200 rounded-lg p-1"
          role="tablist"
        >
          {FILTERS.map((option) => (
            <button
              key={option.key || 'all'}
              type="button"
              role="tab"
              aria-selected={filter === option.key}
              onClick={() => {
                setFilter(option.key);
                setSkip(0);
              }}
              className={`px-3 py-1 rounded-md text-xs ${
                filter === option.key
                  ? 'bg-white text-claret-500 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {(() => {
        if (isLoading) return <div className="h-40 animate-pulse bg-gray-50" />;
        if (error) {
          return (
            <p className="px-5 pb-5 text-sm text-gray-600">
              We couldn&apos;t load the messages.
            </p>
          );
        }
        if (messages.length === 0) {
          return (
            <p className="px-5 pb-5 text-sm text-gray-500">
              No messages{filter ? ` with status ${filter}` : ''}.
            </p>
          );
        }
        return (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 bg-gray-50 border-y border-gray-200">
                    <th className="py-2 pl-5 pr-2 font-medium">Mobile</th>
                    <th className="py-2 px-2 font-medium">Message</th>
                    <th className="py-2 px-2 font-medium">Status</th>
                    <th className="py-2 px-2 font-medium">Response</th>
                    <th className="py-2 pl-2 pr-5 font-medium">Attempted</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 last:border-0 align-top"
                    >
                      <td className="py-3 pl-5 pr-2 font-mono text-xs text-gray-700 whitespace-nowrap">
                        {row.mobile}
                      </td>
                      <td className="py-3 px-2 text-gray-900 max-w-md whitespace-pre-wrap break-words">
                        {row.message}
                      </td>
                      <td className="py-3 px-2">
                        <StatusChip status={row.status} kind="message" />
                      </td>
                      <td className="py-3 px-2 text-xs text-gray-600">
                        {row.response_description || '–'}
                      </td>
                      <td className="py-3 pl-2 pr-5 text-xs text-gray-600 whitespace-nowrap">
                        {formatDateTime(row.attempted_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-100 text-xs text-gray-600">
              <span>
                {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of{' '}
                {total.toLocaleString()}
              </span>
              <button
                type="button"
                disabled={skip === 0}
                onClick={() => setSkip(Math.max(skip - PAGE_SIZE, 0))}
                className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={skip + PAGE_SIZE >= total}
                onClick={() => setSkip(skip + PAGE_SIZE)}
                className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </>
        );
      })()}
    </section>
  );
}

function SmsCampaignDetail() {
  const { campaignId } = useParams();

  const {
    data: campaign,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['sms-campaign', campaignId],
    async () => (await axiosInstancev2.get(`${SMS_BASE}/${campaignId}`)).data,
    {
      refetchOnWindowFocus: false,
      retry: false,
      refetchInterval: (result) => (isActive(result?.status) ? POLL_MS : false),
    }
  );

  return (
    <MainContent full>
      <div className="space-y-5 pb-6">
        <div className={`${CARD} px-5 py-4`}>
          <nav className="text-xs text-gray-500 mb-1">
            <Link to="/sms" className="hover:text-claret-500">
              Bulk SMS
            </Link>{' '}
            › <span className="text-gray-900">Campaign</span>
          </nav>
          {campaign ? (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-lg font-semibold text-gray-900">
                  {describeAudience(campaign.audience)}
                </h1>
                <StatusChip status={campaign.status} />
              </div>
              <p className="text-xs text-gray-500">
                Created by {campaign.created_by} on{' '}
                {formatDateTime(campaign.created_at)}
                {campaign.completed_at &&
                  ` · completed ${formatDateTime(campaign.completed_at)}`}
                {campaign.skipped_count > 0 &&
                  ` · ${campaign.skipped_count.toLocaleString()} skipped (no valid Kenyan number)`}
              </p>
            </>
          ) : (
            <h1 className="text-lg font-semibold text-gray-900">Campaign</h1>
          )}
        </div>

        {(() => {
          if (isLoading) {
            return (
              <div
                className={`${CARD} h-64 animate-pulse`}
                aria-label="Loading"
              />
            );
          }
          if (error) {
            const status = error?.response?.status;
            if (status === 404) {
              return (
                <div
                  className={`${CARD} p-8 text-center text-sm text-gray-600`}
                >
                  This campaign wasn&apos;t found.{' '}
                  <Link to="/sms" className="text-claret-500 underline">
                    Back to Bulk SMS
                  </Link>
                </div>
              );
            }
            return <SmsUnavailable status={status} onRetry={refetch} />;
          }
          return (
            <>
              <Progress campaign={campaign} />
              <section className={`${CARD} p-5`}>
                <h2 className="text-base font-semibold text-gray-900 mb-2">
                  Message template
                </h2>
                <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                  {campaign.message}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  {(campaign.estimated_segments ?? 0).toLocaleString()} SMS
                  parts
                  {campaign.estimated_cost_kes !== null &&
                    campaign.estimated_cost_kes !== undefined &&
                    ` · estimated KES ${Number(
                      campaign.estimated_cost_kes
                    ).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}
                </p>
              </section>
              <MessagesTable
                campaignId={campaignId}
                active={isActive(campaign.status)}
              />
            </>
          );
        })()}
      </div>
    </MainContent>
  );
}

export default SmsCampaignDetail;
